import { cweaCartsFetch } from '@libs/api-proxy-client';
import {
    logSentryError,
    mergeCartNotificationsPreservingValidateOnLoad,
    SentryAcpSignInErrors as SentryErrors,
} from '@libs/common-next';
import type { CartUI, CartValidationResponse } from '@libs/common-node';
import { HttpMethod, HttpStatus } from '@libs/core';

import {
    isCartMergeValidationFailureResponse,
    type RecoverDiscontinuedFromCartMergeValidationArgs,
    recoverDiscontinuedItemsFromCartMergeValidation,
} from './recoverDiscontinuedItemsFromCartMergeValidation';

export function notificationsPreservedFromAuth(
    authNotifications: CartUI['notifications'],
    laterNotifications: CartUI['notifications']
) {
    if (laterNotifications?.length) {
        return mergeCartNotificationsPreservingValidateOnLoad(authNotifications, laterNotifications);
    }

    return authNotifications ?? laterNotifications ?? [];
}

export type ValidateCartMergeResult = { success: boolean; cart?: CartUI | null };

export type ValidateCartMergeArgs = {
    mergedCart?: CartUI | null;
    onApiFail: () => void;
    handleCartError: RecoverDiscontinuedFromCartMergeValidationArgs['handleCartError'];
    setNotifications: RecoverDiscontinuedFromCartMergeValidationArgs['setNotifications'];
    removeLineItemsFromCartState: RecoverDiscontinuedFromCartMergeValidationArgs['removeLineItemsFromCartState'];
    refetchCart: RecoverDiscontinuedFromCartMergeValidationArgs['refetchCart'];
    clearStockErrors: RecoverDiscontinuedFromCartMergeValidationArgs['clearStockErrors'];
    clearErrors: RecoverDiscontinuedFromCartMergeValidationArgs['clearErrors'];
    enrichCart: (cart: CartUI, previous?: CartUI | null) => Promise<CartUI>;
    setCart: (cart: CartUI) => void;
};

function applyPreservedMergeCartState(
    mergedCart: CartUI,
    overlayCart: CartUI | undefined,
    laterNotifications: CartUI['notifications'],
    setCart: ValidateCartMergeArgs['setCart'],
    setNotifications: ValidateCartMergeArgs['setNotifications']
): CartUI {
    const preservedNotifications = notificationsPreservedFromAuth(mergedCart.notifications, laterNotifications);
    const cartAfterValidation: CartUI = {
        ...mergedCart,
        ...overlayCart,
        merged: mergedCart.merged ?? overlayCart?.merged,
        notifications: preservedNotifications,
    };
    setCart(cartAfterValidation);

    if (preservedNotifications.length) {
        setNotifications(prev => mergeCartNotificationsPreservingValidateOnLoad(prev, preservedNotifications));
    }

    return cartAfterValidation;
}

export async function validateCartMerge({
    mergedCart,
    onApiFail,
    handleCartError,
    setNotifications,
    removeLineItemsFromCartState,
    refetchCart,
    clearStockErrors,
    clearErrors,
    enrichCart,
    setCart,
}: ValidateCartMergeArgs): Promise<ValidateCartMergeResult> {
    if (!mergedCart?.id) {
        return { success: false };
    }

    try {
        const result = await cweaCartsFetch<CartValidationResponse>({
            method: HttpMethod.PUT,
            pathname: `carts/${mergedCart.id}/validate`,
            additionalSuccessCodes: [HttpStatus.SEE_OTHER, HttpStatus.UNPROCESSABLE_ENTITY],
        });

        if (result == null) {
            return { success: false };
        }

        if (result instanceof Error || ('status' in result && result.status === HttpStatus.INTERNAL_SERVER_ERROR)) {
            onApiFail();
            return { success: false };
        }

        if (isCartMergeValidationFailureResponse(result)) {
            await recoverDiscontinuedItemsFromCartMergeValidation({
                validationResponse: result,
                mergedCart,
                handleCartError,
                setNotifications,
                removeLineItemsFromCartState,
                refetchCart,
                clearStockErrors,
                clearErrors,
            });
            return { success: false, cart: mergedCart };
        }

        if ('id' in result && typeof result.id === 'string') {
            const validatedCart = await enrichCart(result, mergedCart);
            // validateCartMerge does not re-emit qtyUpdate after /auth already corrected quantity.
            return {
                success: true,
                cart: applyPreservedMergeCartState(
                    mergedCart,
                    validatedCart,
                    validatedCart.notifications ?? result.notifications,
                    setCart,
                    setNotifications
                ),
            };
        }

        // 303 `{ size, timeout }` means quantity was already corrected; there is no cart body to enrich.
        if ('size' in result && 'timeout' in result) {
            return {
                success: true,
                cart: applyPreservedMergeCartState(mergedCart, undefined, undefined, setCart, setNotifications),
            };
        }

        return { success: false };
    } catch (error) {
        logSentryError(error, SentryErrors.SignInForm.ValidateCartMerge);
        return { success: false };
    }
}
